import React from "react";
import Reveal from "./ui/Reveal";
import SectionLabel from "./ui/SectionLabel";
import SpotlightCard from "./ui/SpotlightCard";

const TONES = {
  teal: {
    spot: "rgba(0,178,169,.16)",
    line: "rgba(0,178,169,.7)",
    chip: "bg-tealVitality/10 text-tealVitality",
    hover: "group-hover:bg-tealVitality group-hover:text-white",
    blob: "bg-tealVitality/10",
    text: "text-tealVitality",
  },
  amber: {
    spot: "rgba(244,163,64,.18)",
    line: "rgba(244,163,64,.75)",
    chip: "bg-pawOrange/10 text-pawOrange",
    hover: "group-hover:bg-pawOrange group-hover:text-white",
    blob: "bg-pawOrange/10",
    text: "text-pawOrange",
  },
  blue: {
    spot: "rgba(59,130,246,.16)",
    line: "rgba(59,130,246,.7)",
    chip: "bg-blue-500/10 text-blue-600",
    hover: "group-hover:bg-blue-600 group-hover:text-white",
    blob: "bg-blue-500/10",
    text: "text-blue-600",
  },
};

const Icon = ({ d, className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const PATHS = {
  users:
    "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  record:
    "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  grooming:
    "M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z",
  box: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
  cash: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  chat: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
};

const FEATURES = [
  {
    tone: "teal",
    icon: PATHS.users,
    title: "Gestão de Clientes",
    text: "CRM completo para fidelizar tutores. Histórico de pagamentos, preferências e comunicação automatizada via WhatsApp.",
    span: "lg:col-span-3",
    tags: ["CRM", "Histórico", "WhatsApp"],
  },
  {
    tone: "amber",
    icon: PATHS.record,
    title: "Prontuário Digital",
    text: "Registro clínico detalhado, histórico de vacinas, atendimentos veterinários e alertas de retorno integrados.",
    span: "lg:col-span-3",
    tags: ["Vacinas", "Clínico", "Alertas"],
  },
  {
    tone: "blue",
    icon: PATHS.grooming,
    title: "Banho & Tosa",
    text: "Agenda de grooming com controle de tempos, taxi-dog e pacotes de serviços.",
    span: "lg:col-span-2",
    tags: ["Taxi-dog", "Pacotes"],
  },
  {
    tone: "teal",
    icon: PATHS.cash,
    title: "Fluxo de Caixa",
    text: "Entradas, saídas e formas de pagamento registradas automaticamente a cada atendimento.",
    span: "lg:col-span-2",
    tags: ["Financeiro"],
  },
  {
    tone: "amber",
    icon: PATHS.box,
    title: "Controle de Estoque",
    text: "Rações, medicamentos e produtos com baixa automática e aviso de reposição.",
    span: "lg:col-span-2",
    tags: ["Reposição"],
  },
];

const FeatureCard = ({ feature, index }) => {
  const tone = TONES[feature.tone];

  return (
    <Reveal
      delay={index * 90}
      className={`${feature.span} col-span-1 md:col-span-2`}
    >
      <SpotlightCard
        spot={tone.spot}
        line={tone.line}
        className="group flex h-full flex-col overflow-hidden rounded-[32px] border border-midnight/[.06] bg-white p-8 shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-lift lg:p-10"
      >
        {/* Mancha decorativa que cresce no hover */}
        <div
          className={`pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-bl-[110px] ${tone.blob} transition-transform duration-700 group-hover:scale-[1.7]`}
        />

        <div
          className={`mb-7 flex h-14 w-14 items-center justify-center rounded-2xl transition-all duration-500 group-hover:-rotate-6 group-hover:scale-110 ${tone.chip} ${tone.hover}`}
        >
          <Icon d={feature.icon} className="h-7 w-7" />
        </div>

        <h3 className="mb-3 font-display text-2xl font-bold text-midnight">
          {feature.title}
        </h3>
        <p className="mb-6 flex-1 leading-relaxed text-midnight/55">
          {feature.text}
        </p>

        <div className="flex flex-wrap gap-2">
          {feature.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-midnight/[.045] px-3 py-1 text-xs font-semibold text-midnight/50 transition-colors duration-300 group-hover:bg-midnight/[.08]"
            >
              {tag}
            </span>
          ))}
        </div>
      </SpotlightCard>
    </Reveal>
  );
};

const Features = () => (
  <section id="features" className="relative overflow-hidden bg-softIvory py-28">
    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-midnight/10 to-transparent" />
    <div className="pointer-events-none absolute left-1/2 top-0 -z-0 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-tealVitality/[.07] blur-[120px]" />

    <div className="relative mx-auto max-w-7xl px-6">
      <div className="mx-auto mb-20 max-w-3xl text-center">
        <Reveal>
          <SectionLabel tone="amber">Funcionalidades</SectionLabel>
        </Reveal>
        <Reveal delay={90}>
          <h2 className="mb-6 mt-6 font-display text-[clamp(2rem,4.4vw,3.25rem)] font-extrabold leading-[1.08] text-midnight">
            Tudo que o seu pet shop faz,
            <br className="hidden sm:block" /> em{" "}
            <span className="text-gradient">um só lugar</span>
          </h2>
        </Reveal>
        <Reveal delay={180}>
          <p className="text-lg leading-relaxed text-midnight/55">
            Ferramentas desenhadas para o fluxo real de clínicas veterinárias e
            pet shops — não um ERP genérico adaptado às pressas.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-6">
        {FEATURES.map((feature, i) => (
          <FeatureCard key={feature.title} feature={feature} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Features;
